import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

import { FiLock } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { IoMdEyeOff } from "react-icons/io";

import { AuthContext } from "../Auth/AuthProvider";
import LoadingScreen from "../Componentes/LoadingScreen";
import useApi from "../hooks/useApi";

const CHANGE_PASSWORD_URL = "/Painel/API/Auth/change_password.php";
const MIN_LENGTH = 6;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    overflow: hidden;
    background:
        linear-gradient(170deg, rgba(37, 99, 235, 0.22), transparent 55%),
        #263b4b;
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

const Card = styled.div`
    width: 100%;
    max-width: 420px;
    box-sizing: border-box;
    padding: 40px 36px;
    border-radius: 16px;
    background-color: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 20px;
    animation: ${fadeIn} 0.4s ease both;
`;

const BrandLogo = styled.img`
    height: 40px;
    align-self: flex-start;
`;

const Heading = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.7);
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.02em;
`;

const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 46px;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: #4a90e2;
        box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.25);
    }
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 100%;
    color: rgba(0, 0, 0, 0.45);

    svg {
        width: 18px;
        height: 18px;
    }
`;

const Input = styled.input`
    flex: 1;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    box-sizing: border-box;
    padding-right: 12px;
    font-size: 14px;
    color: #1f2937;
    font-family: inherit;

    &::placeholder {
        color: #9ca3af;
    }
`;

const ShowPass = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 100%;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    transition: color 0.15s ease;

    &:hover {
        color: #2563eb;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: #ffb4b4;
    min-height: 16px;
`;

const PrimaryButton = styled.button`
    height: 46px;
    margin-top: 6px;
    border: none;
    border-radius: 10px;
    background-color: #4caf50;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
        background-color: #45a049;
    }

    &:active:not(:disabled) {
        transform: scale(0.99);
    }

    &:disabled {
        background-color: #c1c1c1;
        cursor: not-allowed;
    }
`;

const SecondaryButton = styled.button`
    height: 42px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    background-color: transparent;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
        border-color: #fff;
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const TrocarSenha = () => {
    const navigate = useNavigate();
    const api = useApi();
    const { user, loading, checkAuth, logout } = useContext(AuthContext);

    const [novaSenha, setNovaSenha] = useState("");
    const [confirmarSenha, setConfirmarSenha] = useState("");
    const [showNova, setShowNova] = useState(false);
    const [showConfirmar, setShowConfirmar] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (loading) {
        return <LoadingScreen />;
    }

    if (user && !user.DEVE_TROCAR_SENHA) {
        navigate("/modules", { replace: true });
        return <LoadingScreen />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitting) return;

        setError("");

        if (novaSenha.length < MIN_LENGTH) {
            setError(`A nova senha deve ter no mínimo ${MIN_LENGTH} caracteres.`);
            return;
        }

        if (novaSenha !== confirmarSenha) {
            setError("As senhas não coincidem.");
            return;
        }

        setSubmitting(true);

        try {
            await api.post(CHANGE_PASSWORD_URL, {
                nova_senha: novaSenha,
                confirmar_senha: confirmarSenha,
            });

            await checkAuth();
            navigate("/modules", { replace: true });
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message || "Erro ao alterar a senha. Tente novamente."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Page>
            <Card>
                <BrandLogo src="/Images/Logo.png" alt="Palmont" />

                <Heading>
                    <Title>Defina uma nova senha</Title>
                    <Subtitle>
                        Por segurança, você precisa alterar a senha antes de continuar
                        acessando o sistema.
                    </Subtitle>
                </Heading>

                <Form onSubmit={handleSubmit}>
                    <Field>
                        <FieldLabel>NOVA SENHA</FieldLabel>
                        <InputWrapper>
                            <IconBox>
                                <FiLock />
                            </IconBox>
                            <Input
                                type={showNova ? "text" : "password"}
                                placeholder="Nova senha"
                                value={novaSenha}
                                onChange={(e) => setNovaSenha(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                            <ShowPass
                                type="button"
                                onClick={() => setShowNova((prev) => !prev)}
                                aria-label={showNova ? "Ocultar senha" : "Mostrar senha"}
                            >
                                {showNova ? <IoEye /> : <IoMdEyeOff />}
                            </ShowPass>
                        </InputWrapper>
                    </Field>

                    <Field>
                        <FieldLabel>CONFIRMAR NOVA SENHA</FieldLabel>
                        <InputWrapper>
                            <IconBox>
                                <FiLock />
                            </IconBox>
                            <Input
                                type={showConfirmar ? "text" : "password"}
                                placeholder="Repita a nova senha"
                                value={confirmarSenha}
                                onChange={(e) => setConfirmarSenha(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                            <ShowPass
                                type="button"
                                onClick={() => setShowConfirmar((prev) => !prev)}
                                aria-label={
                                    showConfirmar ? "Ocultar senha" : "Mostrar senha"
                                }
                            >
                                {showConfirmar ? <IoEye /> : <IoMdEyeOff />}
                            </ShowPass>
                        </InputWrapper>
                    </Field>

                    <ErrorMessage>{error}</ErrorMessage>

                    <PrimaryButton type="submit" disabled={submitting}>
                        {submitting ? "SALVANDO..." : "SALVAR NOVA SENHA"}
                    </PrimaryButton>

                    <SecondaryButton type="button" onClick={logout}>
                        SAIR
                    </SecondaryButton>
                </Form>
            </Card>
        </Page>
    );
};

export default TrocarSenha;
